
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

import { getProviderManagementAPI, getContactAPI, updateContactAPI } from "@/api";
import { Contact, ProviderManagement } from "@/api/interfaces";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { useSuccessHandler } from "@/hooks";
import { getThemeButton, getThemeURL } from "@/utils";

import { CheckboxGroup, Form, SubmitButton, useForm } from "capitalrx-components-2.0/lib/forms";
import { Button, Buttons } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Subheading, Title } from "capitalrx-components-2.0/lib/typography";

import styles from "./Notifications.module.scss";
import { BackLink } from "../BackLink";

export const Notifications: FC = () => {
  const form = useForm<Contact.NotificationRequest>({ defaultValues: { notification_destinations: ["authorized_official_email"] } });
  const navigate = useNavigate();
  const { isUserLoggedIn, getAuthToken, getUser } = useContext(AuthenticationContext);
  const [provider, setProvider] = useState<ProviderManagement.ProviderSchema>();
  const successHandler = useSuccessHandler();
  const { primary_color, primary_hover_color, secondary_hover_color } = useContext(ThemeSelectorContext);

  const notificationOptions = ["pa_case_change", "pa_action_required"];
  const notificationDestinations = useWatch({ name: "notification_destinations", control: form.control });

  const checkUser = async () =>{
    await getUser();
  };

  const getProvider = useCallback(async () => {
      const pmResponse = await getProviderManagementAPI(getAuthToken());
      setProvider(pmResponse.provider);
  }, []);

  const submitHandler = async (data: Contact.NotificationRequest): Promise<void> => {
    try{
      const payload = {
        active_notifications: data.active_notifications,
        notification_destinations: data.notification_destinations,
      };
      await updateContactAPI(payload, getAuthToken());
      successHandler({ message: "Saved settings" });
    } catch (error) {
      console.error(error);
      return;
    }
  };

  useEffect(() => {
    if (!isUserLoggedIn()) {
      navigate(getThemeURL("LOGIN"));
    }

    const fetchData = async () => {
      await checkUser();
      await getProvider();

      const contactResponse = await getContactAPI(getAuthToken());

      // Populate form if existing results found
      if (contactResponse?.active_notifications?.length !== 0) {
        form.setValue("active_notifications", contactResponse.active_notifications);
      };
      if (contactResponse?.notification_destinations?.length !== 0) {
        form.setValue("notification_destinations", contactResponse.notification_destinations);
      };
    };

    fetchData();
  }, [isUserLoggedIn, getUser, navigate, getProvider, getAuthToken]);

  const handleSelectAll = useCallback(() => {
    form.setValue("active_notifications", notificationOptions);
  }, [form]);

  const handleDeselectAll = useCallback(() => {
    form.setValue("active_notifications", []);
  }, [form]);

  const handleCancelClick = useCallback(() => {
    navigate(getThemeURL("HOME"));
  }, [navigate]);

  return (
    <div className={styles.NotificationsContainer}>
      <Form form={form} onSubmit={submitHandler}>
      <Stack spacing="large">
        <Stack spacing="none">
          <BackLink linkText="Home" />
          <Stack spacing="small">
            <Title>Notification Settings</Title>
            <Body size="medium">Select preferred contact method and which notifications are sent</Body>
          </Stack>
        </Stack>
        <Stack>
          <Stack spacing="small">
            <Subheading>Contact Method</Subheading>
            {notificationDestinations.length === 0 && (
              <Body className={styles.ValidationText} size="small">At least one email must be selected</Body>
            )}
          </Stack>
          {provider?.authorized_official_email === provider?.primary_contact_email && (
            <CheckboxGroup.Field hideLabel name="notification_destinations" unpadded>
              <CheckboxGroup.Option value="authorized_official_email"> Primary contact: {provider?.authorized_official_email}</CheckboxGroup.Option>
              <CheckboxGroup.Option value="secondary_contact_email">Secondary contact: {provider?.secondary_contact_email}</CheckboxGroup.Option>
            </CheckboxGroup.Field>
          )}
          {provider?.authorized_official_email !== provider?.primary_contact_email && (
            <CheckboxGroup.Field hideLabel name="notification_destinations" unpadded>
              <CheckboxGroup.Option value="authorized_official_email"> User: {provider?.authorized_official_email}</CheckboxGroup.Option>
              <CheckboxGroup.Option value="primary_contact_email">Primary contact: {provider?.primary_contact_email}</CheckboxGroup.Option>
              <CheckboxGroup.Option value="secondary_contact_email">Secondary contact: {provider?.secondary_contact_email}</CheckboxGroup.Option>
            </CheckboxGroup.Field>
          )}
        </Stack>
        <Stack spacing="small">
        <Shelf>
          <Heading>Notifications</Heading>
          <Shelf.Spacer />
          <Button appearance={Buttons.Functional.Tertiary.Large} style={{ color: primary_color }} onClick={handleSelectAll}>Select All</Button>
          <Button appearance={Buttons.Functional.Tertiary.Large} style={{ color: primary_color }} onClick={handleDeselectAll}>Deselect All</Button>
        </Shelf>
        <Body size="small">Sent in bulk in a daily email at 12pm EST</Body>
        </Stack>
        <Stack>
          <Subheading>Prior Authorizations</Subheading>
          <CheckboxGroup.Field orientation="vertical" name="active_notifications" hideLabel unpadded>
            <CheckboxGroup.Option value="pa_case_change">Case status change (approvals and denials)</CheckboxGroup.Option>
            <CheckboxGroup.Option value="pa_action_required">Case action required</CheckboxGroup.Option>
          </CheckboxGroup.Field>
        </Stack>
        <Shelf spacing="small">
          <SubmitButton appearance={getThemeButton("primary", primary_color, primary_hover_color)}>Save</SubmitButton>
          <Button appearance={getThemeButton("secondary", primary_color, secondary_hover_color)} onClick={handleCancelClick}>Cancel</Button>
          <Shelf.Spacer />
        </Shelf>
      </Stack>
      </Form>
    </div>
  );
};

export default Notifications;
